import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Copy, MessageSquare, RotateCcw } from "lucide-react";
import { api, errMsg, getRefCode, sessionId, track } from "@/lib/api";
import { Btn, Eyebrow, Ornament, LiveDot } from "@/components/brand";
import { Field, Input, Pills, Consent, FormError } from "@/components/forms";
import { useSite, useSeo } from "@/components/site";

const GOALS = ["Repair", "Inquiry Sweep", "Funding", "LLC"];

export function Consultation() {
  useSeo("Free Consultation", "Book a free, no-obligation consultation with Seven. I review every request personally.");
  const [q] = useSearchParams();
  const { data: site } = useSite();
  const ref = getRefCode();
  const [f, setF] = useState({ name: "", phone: "", email: "", goals: [], bestTime: "", source: ref ? "Referral" : "", notes: "", plan: q.get("plan") || "" });
  const [consent, setConsent] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);
  const set = (k) => (e) => { if (!started) { setStarted(true); track("form_start", { label: "consultation" }); } setF({ ...f, [k]: e?.target ? e.target.value : e }); };
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/public/leads", { ...f, consent, referralCode: ref, sessionId: sessionId() });
      track("form_submit", { label: "consultation" });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  const plan = site?.pricing?.find((p) => p.key === f.plan);
  if (done) return (
    <section className="bg-cream min-h-[70vh] flex items-center py-20" data-testid="consult-confirmation">
      <div className="max-w-2xl mx-auto px-5 text-center fade-up">
        <span className="inline-flex w-14 h-14 bg-ink text-gold items-center justify-center"><Check /></span>
        <h1 className="f-display text-4xl sm:text-5xl mt-6">I'll reach out shortly.</h1>
        <p className="text-muted7 mt-4 text-lg">Thank you, {f.name.split(" ")[0]}. I review every request personally — expect a call or text from me soon.</p>
        <div className="flex flex-wrap justify-center gap-3 mt-9">
          {site?.calendlyLink && <Btn as="a" href={site.calendlyLink} target="_blank" rel="noopener noreferrer" variant="gold" arrow data-testid="confirm-calendly-btn">Pick a time on my calendar</Btn>}
          <Btn as={Link} to={`/register?email=${encodeURIComponent(f.email)}`} arrow data-testid="create-portal-login-btn">Create Portal Login</Btn>
        </div>
        <Ornament className="mt-12" />
      </div>
    </section>
  );
  return (
    <section className="grid lg:grid-cols-[.9fr_1.1fr] min-h-[calc(100vh-72px)]">
      <div className="bg-ink text-white px-6 sm:px-12 py-16 lg:py-24 flex flex-col justify-center">
        <Eyebrow light>Free consultation</Eyebrow>
        <h1 className="f-display text-4xl sm:text-5xl mt-4">Elevate your financial standing.</h1>
        <p className="text-white/70 mt-5 text-lg max-w-md">I review every request personally and reach out to you directly. Free. No commitments.</p>
        <ul className="mt-10 space-y-4 max-w-md">
          {["One complete sweep", "Open line to Seven", "$20k–$100k+ funding"].map((t) => <li key={t} className="border border-gold/60 px-5 py-4 f-display text-xl">{t}</li>)}
        </ul>
        <LiveDot light className="mt-10" label="Seven is online · talk to me anytime" />
      </div>
      <form onSubmit={submit} className="bg-white px-6 sm:px-12 py-14 lg:py-20 space-y-6 max-w-2xl w-full" data-testid="consult-form">
        {plan && <p className="chip chip-dark !py-2" data-testid="selected-plan">Selected plan: {plan.name} · ${plan.price.toLocaleString()}</p>}
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Full name" id="c-name" required><Input id="c-name" testid="consult-name" value={f.name} onChange={set("name")} required autoComplete="name" /></Field>
          <Field label="Phone" id="c-phone" required><Input id="c-phone" testid="consult-phone" type="tel" value={f.phone} onChange={set("phone")} required autoComplete="tel" /></Field>
        </div>
        <Field label="Email" id="c-email" required><Input id="c-email" testid="consult-email" type="email" value={f.email} onChange={set("email")} required autoComplete="email" /></Field>
        <Field label="Your goals (pick any)"><Pills options={GOALS} value={f.goals} onChange={set("goals")} testid="consult-goal" /></Field>
        <Field label="Best time to call"><Pills options={["Morning", "Afternoon", "Evening"]} value={f.bestTime} onChange={set("bestTime")} multi={false} testid="consult-time" /></Field>
        <Field label="How did you hear about us?" id="c-src">
          <select id="c-src" className="field" value={f.source} onChange={set("source")} data-testid="consult-source">
            <option value="">Select one</option>{["TikTok", "Referral", "Google", "Other"].map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Anything I should know (optional)" id="c-notes"><textarea id="c-notes" rows={4} maxLength={2000} className="field" value={f.notes} onChange={set("notes")} data-testid="consult-notes" /></Field>
        <Consent checked={consent} onChange={setConsent} testid="consult-consent" />
        <FormError msg={err} />
        <Btn type="submit" arrow disabled={busy || !consent} className="w-full sm:w-auto" data-testid="consult-submit-btn">{busy ? "Sending…" : "Request My Free Consultation"}</Btn>
      </form>
    </section>
  );
}

export function Refer() {
  useSeo("Refer & Earn $100", "Earn $100 for every person you send to Se7en Capital Group who enrolls. No sign-up needed.");
  const { data: site } = useSite();
  const payout = site?.referralPayout || 100;
  const empty = { referredName: "", referredPhone: "", goals: [], referrerName: "", referrerPhone: "", cashtag: "" };
  const [f, setF] = useState(empty);
  const [consent, setConsent] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [copied, setCopied] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/public/referrals", { ...f, consent });
      track("form_submit", { label: "referral" });
      setDone(data);
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  const link = done ? `https://se7encapital.com/r/${done.code}` : "";
  return (
    <>
      <section className="bg-ink text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Eyebrow light>Refer & earn</Eyebrow>
          <h1 className="f-display text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-3xl">Earn <span className="gold-foil-dark font-semibold">${payout}</span> for every person you send my way.</h1>
          <p className="text-white/70 mt-5 text-lg">No sign-up. No account. Anyone can refer.</p>
        </div>
      </section>
      <section className="bg-cream py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1.2fr_.8fr] gap-10">
          {done ? (
            <div className="bg-white border border-tanline p-8 sm:p-10 fade-up" data-testid="refer-success">
              <h2 className="f-display text-3xl sm:text-4xl">Thank you — I'll take it from here.</h2>
              <p className="text-muted7 mt-3">Here's your personal link. Anyone who books through it is credited to you.</p>
              <div className="mt-6 flex flex-col sm:flex-row gap-2">
                <input readOnly value={link} className="field f-mono text-sm" data-testid="refer-share-link" />
                <button className="btn btn-black" onClick={() => { navigator.clipboard.writeText(link); setCopied(true); }} data-testid="refer-copy-btn"><Copy className="w-4 h-4" />{copied ? "Copied" : "Copy"}</button>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <a className="btn btn-outline" href={`sms:?&body=${encodeURIComponent(`I've been working with Seven at Se7en Capital Group — book a free consult here: ${link}`)}`} data-testid="refer-text-btn"><MessageSquare className="w-4 h-4" />Text it</a>
                <button className="btn btn-outline" onClick={() => { setDone(null); setF({ ...empty, referrerName: f.referrerName, referrerPhone: f.referrerPhone, cashtag: f.cashtag }); setCopied(false); }} data-testid="refer-another-btn"><RotateCcw className="w-4 h-4" />Refer another</button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-tanline p-7 sm:p-10 space-y-6" data-testid="refer-form">
              <p className="eyebrow text-red7">Who are you referring?</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Their name" id="r-n" required><Input id="r-n" testid="refer-their-name" value={f.referredName} onChange={set("referredName")} required /></Field>
                <Field label="Their phone" id="r-p" required><Input id="r-p" testid="refer-their-phone" type="tel" value={f.referredPhone} onChange={set("referredPhone")} required /></Field>
              </div>
              <Field label="Their goal (pick one, two or all)"><Pills options={["Repair", "LLC", "Funding"]} value={f.goals} onChange={set("goals")} testid="refer-goal" /></Field>
              <p className="eyebrow text-red7 pt-4 border-t border-tanline">Where do I send your ${payout}?</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Your name" id="r-yn" required><Input id="r-yn" testid="refer-your-name" value={f.referrerName} onChange={set("referrerName")} required /></Field>
                <Field label="Your phone" id="r-yp" required><Input id="r-yp" testid="refer-your-phone" type="tel" value={f.referrerPhone} onChange={set("referrerPhone")} required /></Field>
              </div>
              <Field label="Your $Cashtag" id="r-c" required><Input id="r-c" testid="refer-cashtag" placeholder="$YourCashtag" value={f.cashtag} onChange={set("cashtag")} required /></Field>
              <Consent checked={consent} onChange={setConsent} testid="refer-consent" />
              <FormError msg={err} />
              <Btn type="submit" variant="red" arrow disabled={busy || !consent} data-testid="refer-submit-btn">{busy ? "Sending…" : "Send Referral"}</Btn>
            </form>
          )}
          <aside className="space-y-4">
            {["Send their name, number & goal", "I reach out personally", `They enroll — you get $${payout} on Cash App`].map((t, i) => (
              <div key={t} className="bg-white border border-tanline p-6 flex gap-5 items-start lift"><span className="f-display text-4xl text-gold leading-none">{i + 1}</span><p className="f-display text-xl">{t}</p></div>
            ))}
            <div className="bg-ink text-white p-7 brackets" data-testid="refer-10-card">
              <p className="eyebrow text-gold">Stack it up</p>
              <p className="f-display text-3xl mt-3">Refer 10 people, earn <span className="gold-foil-dark font-semibold">${(payout * 10).toLocaleString()}</span>.</p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

export function RefCode() {
  const code = window.location.pathname.split("/r/")[1]?.split(/[/?#]/)[0] || "";
  useEffect(() => {
    if (code) document.cookie = `s7_ref=${encodeURIComponent(code.toUpperCase())}; max-age=${60 * 24 * 3600}; path=/; SameSite=Lax`;
    track("page_view", { referralCode: code.toUpperCase(), source: "referral" });
    window.location.replace("/consultation");
  }, [code]);
  return <div className="min-h-[50vh]" data-testid="ref-redirect" />;
}

export function Legal({ slug }) {
  const title = slug === "privacy" ? "Privacy Policy" : "Terms of Service";
  useSeo(title);
  const [body, setBody] = useState("");
  useEffect(() => { api.get(`/public/page/${slug}`).then((r) => setBody(r.data.body)).catch(() => {}); }, [slug]);
  return (
    <section className="bg-white py-16 lg:py-24">
      <div className="max-w-3xl mx-auto px-5" data-testid={`legal-${slug}`}>
        <Eyebrow>Se7en Capital Group</Eyebrow>
        <h1 className="f-display text-4xl sm:text-5xl mt-4">{title}</h1>
        <div className="mt-10 space-y-5 text-[15px] leading-relaxed text-muted7">{body.split("\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}</div>
      </div>
    </section>
  );
}
